import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiClient } from "@shared/services/api";
import type { PaginationMeta } from "@shared/types";
import type {
  QuotesListParams,
  QuotesListResponse,
  Quote,
} from "@admin/types/quote";

type QuotesApiResponse = {
  success?: boolean;
  message?: string;
  data:
    | Quote[]
    | {
        items: Quote[];
        pagination?: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
      };
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
};

export function useQuotes(params: QuotesListParams = {}) {
  const {
    page = 1,
    limit = 20,
    sortBy = "createdAt",
    sortOrder = "desc",
    search,
    hasClientData,
    startDate,
    endDate,
  } = params;

  return useQuery({
    queryKey: ["admin", "quotes", { page, limit, sortBy, sortOrder, search, hasClientData, startDate, endDate }],
    queryFn: async (): Promise<QuotesListResponse> => {
      const searchParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sortBy,
        sortOrder,
      });

      if (search) {
        searchParams.append("search", search);
      }
      if (hasClientData !== undefined) {
        searchParams.append("hasClientData", String(hasClientData));
      }
      if (startDate) {
        searchParams.append("startDate", startDate);
      }
      if (endDate) {
        searchParams.append("endDate", endDate);
      }

      const response = await apiClient.get<QuotesApiResponse>(
        `/quote?${searchParams.toString()}`
      );

      // Log de la respuesta exacta del backend sin ninguna transformación
      console.log("🔍 Quote API Response (RAW - sin transformación):", response);
      console.log("🔍 Quote API Response (RAW - JSON):", JSON.stringify(response, null, 2));

      // La estructura real del backend es: { data: { items: [...], pagination: {...} } }
      // Verificar si data es un objeto con items y pagination
      const dataIsObject = response?.data && typeof response.data === "object" && !Array.isArray(response.data) && "items" in response.data;
      
      const items = dataIsObject 
        ? (response.data as { items: Quote[]; pagination?: typeof response.pagination }).items ?? []
        : Array.isArray(response?.data)
        ? response.data
        : [];

      const meta = dataIsObject
        ? (response.data as { items: Quote[]; pagination?: typeof response.pagination }).pagination ?? response?.pagination
        : response?.pagination;

      const computedLimit = meta?.limit ?? limit;
      const computedPage = meta?.page ?? page;
      const computedTotal = meta?.total ?? items.length;
      const computedTotalPages =
        meta?.totalPages ??
        Math.max(1, Math.ceil(computedTotal / computedLimit));

      const pagination: PaginationMeta = {
        total: computedTotal,
        page: computedPage,
        limit: computedLimit,
        totalPages: computedTotalPages,
        hasNext: meta?.hasNext ?? computedPage < computedTotalPages,
        hasPrev: meta?.hasPrev ?? computedPage > 1,
      };

      return {
        success: response.success ?? true,
        message: response.message,
        data: items,
        pagination,
      };
    },
    staleTime: 30 * 1000, // 30 segundos
    placeholderData: keepPreviousData,
  });
}

